/**
 * reviewRedubScope.ts — PLAT-11381 / PLAT-11382 (phase 1 of PLAT-10487,
 * directed dubbing).
 *
 * Two small things, both plain functions: no React, no MobX, no network.
 *
 * 1. `ReviewTranscriptScope` — the word the Translation Review workspace uses
 *    for what it is looking at. `'story'` means the whole story's transcript;
 *    `'clip'` means one clip's slice of it. The workspace used to work this out
 *    for itself by checking which optional props it had been handed. PLAT-11381
 *    makes the host say it outright, because more than one rule now turns on
 *    the answer and a rule that reads a side effect of another feature's props
 *    breaks the moment those props move.
 *
 * 2. `scopeRedubToClip` — what a re-dub started from the review's selection
 *    menu is allowed to cover. A re-dub started here re-records lines of the
 *    STORY's dub, the one every clip of that story plays. On the story path
 *    that is exactly what the reviewer asked for, so nothing is held back. On
 *    the clip path the review is showing one clip, so two things can be wrong
 *    with the selection and both are refused rather than sent:
 *
 *      - the clip cannot be dubbed at all right now (today: it is longer than
 *        the dubbing limit, which the clip surface already measures);
 *      - a selected line has no dubbed audio in this clip yet, so there is
 *        nothing to re-record. Those lines belong to the clip's own "dub this
 *        clip" path, which makes audio where there is none.
 *
 *    Lines whose words were EDITED after they were dubbed are the opposite
 *    case: they do have audio, the audio says the older words, and re-recording
 *    them is the whole point. They pass.
 *
 * WHAT THIS IS NOT. This is ordinary clip scoping: it refuses a clip that is
 * over the dubbing limit, and it skips selected lines this clip has no recorded
 * voice for. It does NOT compare the clip's words against the story's words.
 * That comparison — refusing a whole-story dub for lines whose clip words
 * verifiably differ from the story's — is PLAT-11379, a later phase 4 ticket,
 * and its own document says in as many words that it is deliberately not
 * specified yet. Nothing here should be read as doing that job or as making it
 * unnecessary.
 */

/** What the Translation Review workspace is open over. */
export type ReviewTranscriptScope = 'story' | 'clip';

export interface ScopeRedubToClipInput {
  /** Whether the review is over the whole story or over one clip. */
  scope: ReviewTranscriptScope;
  /**
   * The dub line ids the reviewer's selection covers, in the order the
   * selection resolved them.
   */
  selectedLineIds: readonly string[];
  /**
   * Clip path only — the lines this clip covers that have NO dubbed audio yet
   * (the clip coverage reading's gaps). Undefined means the coverage has not
   * been read, and an unread coverage holds nothing back.
   */
  undubbedLineIds?: readonly string[];
  /**
   * Clip path only — why this clip cannot be dubbed at all, in the surface's
   * own words. Set ⇒ the whole run is refused.
   */
  unavailableReason?: string;
}

export interface ScopeRedubToClipDecision {
  /** The lines to hand to the host's dub popover. Empty ⇒ nothing runs. */
  lineIds: string[];
  /** Selected lines dropped because this clip has no dubbed audio for them. */
  droppedUndubbed: string[];
  /**
   * Why nothing runs, in plain words, or undefined when the run may go ahead.
   * Set only when `lineIds` is empty.
   */
  refusal?: string;
}

/**
 * Decide what a re-dub started from the review's selection menu may cover.
 *
 * Pure. The caller supplies the clip's coverage reading; this function never
 * fetches one.
 */
export function scopeRedubToClip({
  scope,
  selectedLineIds,
  undubbedLineIds,
  unavailableReason,
}: ScopeRedubToClipInput): ScopeRedubToClipDecision {
  const selected = [...new Set(selectedLineIds)];

  if (selected.length === 0) {
    return {
      lineIds: [],
      droppedUndubbed: [],
      refusal: 'Select the words you want re-dubbed first.',
    };
  }

  // The whole story's review re-dubs the whole story's lines. There is no
  // narrower thing for it to be wrong about.
  if (scope === 'story') {
    return { lineIds: selected, droppedUndubbed: [] };
  }

  if (unavailableReason) {
    return { lineIds: [], droppedUndubbed: [], refusal: unavailableReason };
  }

  const undubbed = new Set(undubbedLineIds ?? []);
  const droppedUndubbed = selected.filter(id => undubbed.has(id));
  const lineIds = selected.filter(id => !undubbed.has(id));

  if (lineIds.length === 0) {
    return {
      lineIds: [],
      droppedUndubbed,
      refusal:
        droppedUndubbed.length === 1
          ? 'This line has no dubbed voice in this clip yet, so there is nothing to re-record. Dub the clip first.'
          : 'These lines have no dubbed voice in this clip yet, so there is nothing to re-record. Dub the clip first.',
    };
  }

  return { lineIds, droppedUndubbed };
}
